/* eslint-disable no-spaced-func */
/* eslint-disable func-call-spacing */
import { createAsyncThunk } from "@reduxjs/toolkit";
import { assetsApi, mappingApi } from "@iventis/api/src/api";
import { ResponseError } from "@iventis/api/src/axios.types";
import { Map } from "@iventis/domain-model/model/map";
import { AxiosError } from "axios";
import { getMapObjectAndCommentBounds } from "@iventis/api-helpers-mapping";
import { LocalGeoJson, RouteWaypoint } from "@iventis/map-types";
import { convertMapObjectToLocalGeoJson } from "@iventis/map-engine/src/utilities/state-helpers";
import { queryParams } from "@iventis/utilities";
import { IventisFilterOperator } from "@iventis/data-table";
import { StyleType } from "@iventis/domain-model/model/styleType";
import QueryString from "qs";
import { PagedResult } from "@iventis/domain-model/model/pagedResult";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { Polygon } from "geojson";
import { getAssetsByType } from "@iventis/api-helpers";
import { AssetType } from "@iventis/domain-model/model/assetType";
import { Asset } from "@iventis/domain-model/model/asset";
import { routeWaypointService } from "../services/route-waypoint-service";
import { getProjectDataFields, getSitemaps, getTerrains } from "./map.slice.thunks";
// eslint-disable-next-line iventis/no-root-store-dependencies
import { RootState, RootDispatch } from "./root.store";

/**
 * Calls all of the necessary thunks to load a map
 */
export async function loadMap(mapId: string, signal: AbortSignal, dispatch: RootDispatch) {
    dispatch(getMap({ mapId, signal }));
    dispatch(getProjectDataFields({ signal }));
    dispatch(getMapObjectsForMap({ mapId, pageNumber: 1, signal }));
    dispatch(getSitemaps({ signal }));
    dispatch(getTerrains({ signal }));
    dispatch(getMapBackgrounds({ signal }));
}

/**
 * Gets a basic map
 */
export const getMap = createAsyncThunk<{ map: Map; tileBounds: { objects: Polygon; comments: Polygon } }, { mapId: string; signal: AbortSignal }, { state: RootState }>(
    "Map/getMap",
    async ({ mapId, signal }, { rejectWithValue }) => {
        try {
            const { data: map } = await mappingApi.get<Map>(`/maps/${mapId}`, { signal });
            // If query params has no level, then get from default saved map view
            const selectedLevel = queryParams.level ?? map.savedMapViews.find((view) => view.id === map.defaultSavedMapViewId)?.level?.toString();
            const boundsResponse = await getMapObjectAndCommentBounds(mappingApi, mapId, selectedLevel, signal);
            const tileBounds = {
                objects: boundsResponse.mapObjectBounds,
                comments: boundsResponse.commentBounds,
            };
            incrementMonitoringCount({ name: MetricName.PLAN_LOADED, resourceId: mapId });
            return { map, tileBounds };
        } catch (err) {
            const error: AxiosError<ResponseError> = err;
            return rejectWithValue(error.response?.data);
        }
    }
);

/**
 * Recursively gets all of the map objects for a given map, each time a page is fetched the map state is updated with them, then the next page is requested
 */
export const getMapObjectsForMap = createAsyncThunk<{ geoJSON: LocalGeoJson; finished: boolean }, { mapId: string; pageNumber: number; signal: AbortSignal }, { state: RootState }>(
    "Map/getMapObjectsForMap",
    async ({ mapId, pageNumber, signal }, { rejectWithValue, dispatch }) => {
        try {
            // Create a filter to get map objects for model and line model layers
            const filter = {
                params: {
                    filter: JSON.stringify([{ fieldName: "StyleType", operator: IventisFilterOperator.In, value: [StyleType.Model, StyleType.LineModel] }]),
                    mapId,
                    pageNumber,
                },
                paramsSerializer: (params) => QueryString.stringify(params, { arrayFormat: "repeat" }),
            };

            // Get the map objects for the current page
            const { data: pageOfMapObjects } = await mappingApi.get<PagedResult<MapObject[]>>(`/maps/${mapId}/map_objects/filter`, { ...filter, signal });

            // Get the route waypoints for the map objects
            const mapObjectWayPointsRequest = pageOfMapObjects.results.reduce<Promise<RouteWaypoint[]>[]>((requests, object) => {
                if (object.modeOfTransport != null || object.geoJsonFeature?.properties?.modeOfTransport != null) {
                    requests.push(routeWaypointService.getRouteWaypoints(object.id, mapId));
                }
                return requests;
            }, []);

            const waypoints = await Promise.all(mapObjectWayPointsRequest);

            // Convert the map objects to local geojson
            const parsedObjects = pageOfMapObjects.results
                .map((object, index) => convertMapObjectToLocalGeoJson(object, waypoints[index]))
                .reduce<LocalGeoJson>((acc, object) => {
                    const { layerid } = object.feature.properties;
                    const existingObjects = acc[layerid];
                    acc[layerid] = [...(existingObjects ?? []), object];
                    return acc;
                }, {});

            if (!pageOfMapObjects.lastPage) {
                // Get next page of map objects
                dispatch(getMapObjectsForMap({ mapId, pageNumber: pageNumber + 1, signal }));
            }

            return { geoJSON: parsedObjects, finished: pageOfMapObjects.lastPage };
        } catch (err) {
            const error: AxiosError<ResponseError> = err;
            return rejectWithValue(error.response?.data);
        }
    }
);

/**
 * Gets the bounds of the map objects and comments for a given map
 */
export const getMapObjectAndCommentBoundsThunk = createAsyncThunk<
    { mapObjectBounds: Polygon | ""; commentBounds: Polygon | "" },
    { mapId: string; level: string; signal: AbortSignal },
    { state: RootState }
>("Map/getMapObjectAndCommentBoundsThunk", async ({ mapId, level, signal }, { rejectWithValue }) => {
    try {
        const { mapObjectBounds, commentBounds } = await getMapObjectAndCommentBounds(mappingApi, mapId, level, signal);
        return { mapObjectBounds, commentBounds };
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        return rejectWithValue(error.response?.data);
    }
});

export const getMapBackgrounds = createAsyncThunk<Asset[], { signal: AbortSignal }>("Map/getMapBackgrounds", async ({ signal }, { rejectWithValue }) => {
    try {
        const mapBackgroundAssets = await getAssetsByType([AssetType.MapBackground], assetsApi, signal);
        return mapBackgroundAssets;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        return rejectWithValue(error.response?.data);
    }
});
